import { PicklistOption } from "../blocks/fields/PicklistField.types";
import { positiveLabel } from "./positiveLabel";
import { FieldConfig } from "../render/fieldConfig.types";
import { AttributeDataTypeCode } from "./attributeDataTypes";
import { isCredentialAttribute } from "../config/credentialAttributes";
import {
  EntityAttribute,
  EntityAttributeOption,
} from "./entityAttribute.types";
import { EntityFieldCatalogEntry } from "./entityFieldCatalog.types";

// Option sets are keyed by language id. Falls back to whichever is present rather
// than dropping the attribute.
const PreferredOptionLanguageId = "1033";

/**
 * Audit attributes the service manages itself, on every entity that carries them.
 * `GetEntityAttributes` doesn't yet say which attributes are read-only, so these are
 * defaulted to read-only by name until it does - see `EntityAttribute.ReadOnly`.
 */
const SystemReadOnlyAttributeNames = new Set([
  "createdon",
  "createdby",
  "modifiedon",
  "modifiedby",
]);

const FieldTypeByDataTypeCode: Record<number, FieldConfig["type"]> = {
  [AttributeDataTypeCode.Bit]: "boolean",
  // The metadata can't tell a date from a datetime, and the author can change it.
  [AttributeDataTypeCode.DateTime]: "datetime",
  [AttributeDataTypeCode.Decimal]: "number",
  [AttributeDataTypeCode.Double]: "number",
  [AttributeDataTypeCode.Int]: "number",
  [AttributeDataTypeCode.NText]: "longtext",
  [AttributeDataTypeCode.Money]: "money",
  [AttributeDataTypeCode.Picklist]: "picklist",
  [AttributeDataTypeCode.State]: "picklist",
  [AttributeDataTypeCode.Status]: "picklist",
  [AttributeDataTypeCode.Nvarchar]: "text",
  [AttributeDataTypeCode.MultiSelectPicklist]: "multiselect",
};

function toPicklistOptions(
  options: Record<string, EntityAttributeOption[]> | null | undefined
): PicklistOption[] {
  if (!options) {
    return [];
  }

  const languageIds = Object.keys(options);
  if (languageIds.length === 0) {
    return [];
  }

  const languageId = languageIds.includes(PreferredOptionLanguageId)
    ? PreferredOptionLanguageId
    : languageIds[0];

  return [...(options[languageId] ?? [])]
    .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
    .map((option) => ({ Key: option.Value, Value: option.DisplayName }));
}

/** One entity attribute as a catalog entry, or null when it can't become a usable field. */
export function toCatalogEntry(
  attribute: EntityAttribute
): EntityFieldCatalogEntry | null {
  if (!attribute?.Name || !attribute.DisplayName) {
    return null;
  }

  const name = attribute.Name.toLowerCase();

  if (isCredentialAttribute(name)) {
    return null;
  }

  const base = {
    name,
    // An attribute the API reverses is offered as the permission it grants, label
    // included - the metadata name says the opposite of the value being served.
    label: attribute.ValueReversed
      ? positiveLabel(attribute.DisplayName)
      : attribute.DisplayName,
    kind: "field" as const,
    readOnly:
      attribute.ReadOnly === true || SystemReadOnlyAttributeNames.has(name),
    ...(attribute.ValueReversed ? { valueReversed: true } : {}),
    ...(attribute.IsLogical ? { logical: true } : {}),
  };

  // Checked before the data type: a lookup's code describes the identifier it stores,
  // not the record it points at. Placed with no options, which the author fills in.
  if (attribute.LookupEntity) {
    return {
      ...base,
      fieldType: "lookup",
      lookupEntity: attribute.LookupEntity.toLowerCase(),
      options: [],
    };
  }

  // Metadata names no target for a customer, which may be an account or a contact;
  // accounts are what a contact's Company Name holds, and what can be listed.
  if (attribute.DataTypeCode === AttributeDataTypeCode.Customer) {
    return { ...base, fieldType: "lookup", lookupEntity: "account", options: [] };
  }

  const fieldType = FieldTypeByDataTypeCode[attribute.DataTypeCode];
  if (!fieldType) {
    return null;
  }

  if (fieldType !== "picklist" && fieldType !== "multiselect") {
    return { ...base, fieldType };
  }

  const options = toPicklistOptions(attribute.Options);

  // A list with nothing to pick can't be filled in, and metadata is its only source.
  return options.length === 0 ? null : { ...base, fieldType, options };
}

/**
 * The attributes of any entity as the fields an author can place, built from live
 * metadata rather than a per-entity list. Green4 "custom attributes" need no special
 * handling - they are ordinary attributes an organisation named in a setting.
 */
export function buildEntityFieldCatalog(
  attributes: EntityAttribute[] | null | undefined
): EntityFieldCatalogEntry[] {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes
    .map(toCatalogEntry)
    .filter((entry): entry is EntityFieldCatalogEntry => entry !== null);
}
